import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Download, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Zap, 
  TrendingUp,
  Settings,
  Plus,
  Eye,
  EyeOff
} from 'lucide-react';

const BillingDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCardDetails, setShowCardDetails] = useState({});

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading billing dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'paused': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800',
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const toggleCardDetails = (paymentMethodId) => {
    setShowCardDetails(prev => ({
      ...prev,
      [paymentMethodId]: !prev[paymentMethodId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  // If data failed to load, show error state
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="h-16 w-16 text-yellow-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Unable to Load Billing Data</h2>
          <p className="text-white/80 mb-6">We couldn't retrieve your billing information. This might be temporary.</p>
          <button
            onClick={() => {
              setLoading(true);
              loadDashboardData();
            }}
            className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 overflow-x-hidden w-full">
      <div className="max-w-7xl mx-auto px-4 py-8 w-full overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Billing Dashboard</h1>
          <p className="text-white/80">Manage your subscription, payments, and billing preferences</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Current Plan</p>
                <p className="text-white text-2xl font-bold capitalize">
                  {dashboardData?.current_subscription?.tier || 'Free'}
                </p>
              </div>
              <Zap className="h-8 w-8 text-yellow-300" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Next Billing</p>
                <p className="text-white text-lg font-semibold">
                  {dashboardData?.next_billing_date 
                    ? formatDate(dashboardData.next_billing_date)
                    : 'N/A'
                  }
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-300" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Payment Methods</p>
                <p className="text-white text-2xl font-bold">
                  {dashboardData?.payment_methods?.length || 0}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-green-300" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Active Alerts</p>
                <p className="text-white text-2xl font-bold">
                  {dashboardData?.alerts?.length || 0}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-300" />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {[
                { id: 'overview', label: 'Overview', mobileLabel: 'Overview', icon: TrendingUp },
                { id: 'subscription', label: 'Subscription', mobileLabel: 'Plan', icon: Zap },
                { id: 'payments', label: 'Payment Methods', mobileLabel: 'Payments', icon: CreditCard },
                { id: 'invoices', label: 'Invoices', mobileLabel: 'History', icon: Download },
                { id: 'usage', label: 'Usage', mobileLabel: 'Usage', icon: CheckCircle }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-xl transition-colors whitespace-nowrap text-xs sm:text-sm ${
                    activeTab === tab.id
                      ? 'bg-white text-rose-500'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <tab.icon className="hidden sm:flex h-4 w-4 flex-shrink-0" />
                  <span className="block sm:hidden">{tab.mobileLabel}</span>
                  <span className="hidden sm:block">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Subscription */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Current Subscription</h3>
                {dashboardData?.current_subscription ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/80">Plan:</span>
                      <span className="text-white font-semibold capitalize">
                        {dashboardData.current_subscription.tier}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(dashboardData.current_subscription.status)}`}>
                        {dashboardData.current_subscription.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Started:</span>
                      <span className="text-white">{formatDate(dashboardData.current_subscription.start_date)}</span>
                    </div>
                    {dashboardData.current_subscription.end_date && (
                      <div className="flex justify-between">
                        <span className="text-white/80">Renews:</span>
                        <span className="text-white">{formatDate(dashboardData.current_subscription.end_date)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-white/80">No active subscription</p>
                )}
              </div>

              {/* Recent Activity */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {dashboardData?.recent_invoices?.slice(0, 3).map((invoice) => (
                    <div key={invoice.invoice_id} className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-medium">{formatCurrency(invoice.amount_paid)}</p>
                        <p className="text-white/60 text-sm">{formatDate(invoice.created_at)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                  )) || (
                    <p className="text-white/80">No recent activity</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods Tab */}
          {activeTab === 'payments' && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-white">Payment Methods</h3>
                <button className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-full flex items-center space-x-2 transition-colors">
                  <Plus className="h-4 w-4" />
                  <span>Add Payment Method</span>
                </button>
              </div>

              <div className="space-y-4">
                {dashboardData?.payment_methods?.map((method) => (
                  <div key={method.payment_method_id} className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {method.card_brand?.toUpperCase()} •••• {showCardDetails[method.payment_method_id] ? method.card_last4 : '••••'}
                          </p>
                          <p className="text-white/60 text-sm">
                            Expires {method.card_exp_month}/{method.card_exp_year}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {method.is_default && (
                          <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">
                            Default
                          </span>
                        )}
                        <button
                          onClick={() => toggleCardDetails(method.payment_method_id)}
                          className="text-white/60 hover:text-white p-1"
                        >
                          {showCardDetails[method.payment_method_id] ? 
                            <EyeOff className="h-4 w-4" /> : 
                            <Eye className="h-4 w-4" />
                          }
                        </button>
                        <button className="text-white/60 hover:text-white p-1">
                          <Settings className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) || (
                  <p className="text-white/80 text-center py-8">No payment methods added</p>
                )}
              </div>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Billing History</h3>
              
              {!dashboardData ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="text-white/60">Loading billing history...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData?.recent_invoices && dashboardData.recent_invoices.length > 0 ? (
                    dashboardData.recent_invoices.map((invoice) => (
                      <div key={invoice.invoice_id} className="bg-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{formatCurrency(invoice.amount_paid)}</p>
                            <p className="text-white/60 text-sm">
                              {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                            {invoice.invoice_pdf && (
                              <button className="text-white/60 hover:text-white p-1">
                                <Download className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Download className="h-16 w-16 text-white/20 mx-auto mb-4" />
                      <p className="text-white/80 text-lg mb-2">No Billing History</p>
                      <p className="text-white/60 text-sm">Your billing history will appear here once you make a purchase</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Usage Tab */}
          {activeTab === 'usage' && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Usage Statistics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(dashboardData?.usage_stats || {}).map(([feature, count]) => (
                  <div key={feature} className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-white/80 text-sm capitalize">{feature.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Alerts Section */}
        {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
          <div className="mt-8">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Billing Alerts</h3>
              <div className="space-y-3">
                {dashboardData.alerts.map((alert) => (
                  <div key={alert.alert_id} className={`p-4 rounded-xl border-l-4 ${
                    alert.severity === 'critical' ? 'bg-red-500/20 border-red-400' :
                    alert.severity === 'warning' ? 'bg-yellow-500/20 border-yellow-400' :
                    'bg-blue-500/20 border-blue-400'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{alert.title}</p>
                        <p className="text-white/80 text-sm">{alert.message}</p>
                      </div>
                      {alert.action_required && (
                        <button className="bg-white text-rose-500 px-4 py-2 rounded-full text-sm font-semibold hover:bg-rose-50 transition-colors">
                          Take Action
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingDashboard;